import { z } from 'zod';
import {
  type ApiErrorBody,
  type ApiErrorCode,
  apiErrorBodySchema,
  type CheckoutInput,
  type OrderConfirmation,
  orderConfirmationSchema,
  type Paginated,
  paginatedSchema,
  type Product,
  type ProductListQuery,
  productListSchema,
  productSchema,
} from '@kon/shared';
import { API_ROOT } from './env';

/** Typed error thrown by the API client on any non-success envelope. */
export class ApiError extends Error {
  readonly code: ApiErrorCode;
  readonly status: number;
  readonly details?: Record<string, string[]>;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code;
    if (body.details) this.details = body.details;
  }
}

interface RequestOptions<S extends z.ZodTypeAny> {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  schema: S;
  signal?: AbortSignal;
}

async function request<S extends z.ZodTypeAny>(
  path: string,
  options: RequestOptions<S>,
): Promise<z.infer<S>> {
  const init: RequestInit = {
    method: options.method ?? 'GET',
    credentials: 'include',
    headers: options.body !== undefined ? { 'content-type': 'application/json' } : undefined,
    ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
    ...(options.signal ? { signal: options.signal } : {}),
  };

  const res = await fetch(`${API_ROOT}${path}`, init);

  let json: unknown;
  try {
    json = await res.json();
  } catch {
    throw new ApiError(res.status, { code: 'INTERNAL', message: 'Malformed server response' });
  }

  const envelope = z
    .object({ data: z.unknown(), error: apiErrorBodySchema.nullable() })
    .safeParse(json);

  if (!envelope.success) {
    throw new ApiError(res.status, { code: 'INTERNAL', message: 'Unexpected response shape' });
  }
  if (envelope.data.error) {
    throw new ApiError(res.status, envelope.data.error);
  }

  const parsed = options.schema.safeParse(envelope.data.data);
  if (!parsed.success) {
    // The server violated the shared contract — surface as an internal error.
    throw new ApiError(res.status, {
      code: 'INTERNAL',
      message: 'Response failed validation',
    });
  }
  return parsed.data;
}

/* --------------------------- query serialization --------------------------- */

function toSearchParams(query: Partial<ProductListQuery>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    params.set(key, String(value));
  }
  const s = params.toString();
  return s ? `?${s}` : '';
}

/* -------------------------------- endpoints -------------------------------- */

export const api = {
  listProducts(query: Partial<ProductListQuery>, signal?: AbortSignal): Promise<Paginated<Product>> {
    return request(`/products${toSearchParams(query)}`, {
      schema: paginatedSchema(productSchema),
      ...(signal ? { signal } : {}),
    });
  },

  listFeatured(signal?: AbortSignal): Promise<Product[]> {
    return request('/products/featured', {
      schema: productListSchema,
      ...(signal ? { signal } : {}),
    });
  },

  getProduct(slug: string, signal?: AbortSignal): Promise<Product> {
    return request(`/products/${encodeURIComponent(slug)}`, {
      schema: productSchema,
      ...(signal ? { signal } : {}),
    });
  },

  createOrder(input: CheckoutInput): Promise<OrderConfirmation> {
    return request('/orders', {
      method: 'POST',
      body: input,
      schema: orderConfirmationSchema,
    });
  },

  getOrder(id: string, signal?: AbortSignal): Promise<OrderConfirmation> {
    return request(`/orders/${encodeURIComponent(id)}`, {
      schema: orderConfirmationSchema,
      ...(signal ? { signal } : {}),
    });
  },
};
